import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  signInWithClient,
  useUserClientContext,
  type UserSigninFn,
} from '@/app/clientContext';
import { LogoType } from '@/components';
import { HttpNotFoundError } from '@/features/types';
import { AppShell, Flex } from '@mantine/core';
import { useSession, useUser } from '@descope/react-sdk';
import { useClientCodeFromUrl } from '@/hooks/useClientCodeFromUrl';
import { IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

const useUserSignin = (setError: Dispatch<SetStateAction<string>>) => {
  const { hydrateUserAndClientContext } = useUserClientContext();

  const signInUser = useCallback(
    async (
      token: string,
      user: string,
      email: string,
      internalClientCodeHint?: string
    ) => {
      try {
        await hydrateUserAndClientContext(
          token,
          user,
          email,
          internalClientCodeHint
        );
      } catch (err) {
        if (err instanceof HttpNotFoundError) {
          notifications.show({
            title: 'Error',
            message: 'No client found for provided domain name.',
            color: 'red',
            icon: <IconAlertCircle size="1rem" />,
            autoClose: false, // Keep the notification visible until user dismisses it
          });
          setError(
            'No client found for provided domain name. Unable to sign in.'
          );
        } else {
          notifications.show({
            title: 'Error',
            message: 'Failed to select client or delivery.',
            color: 'red',
            icon: <IconAlertCircle size="1rem" />,
            autoClose: false, // Keep the notification visible until user dismisses it
          });
          setError('Unable to sign in.');
        }
      }
    },
    [setError, hydrateUserAndClientContext]
  );

  return signInUser satisfies UserSigninFn;
};

const ClientSelector = () => {
  const [error, setError] = useState<string>('');
  const isDevelopment = import.meta.env.DEV;
  const signInUser = useUserSignin(setError);
  const hasSignedIn = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { isUserLoaded, canClientSwitch, context } = useUserClientContext();
  const { isAuthenticated } = useSession();
  const { user } = useUser();

  const clientCode = useClientCodeFromUrl();

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (!isUserLoaded) return;
    if (hasSignedIn.current) return;

    if (clientCode) {
      hasSignedIn.current = true;
      const handleSignIn = async () => {
        // Users that cannot switch clients are signed in as themselves, so
        // they get no client code and any `?client=` is dropped on redirect.
        const effectiveClientCode = canClientSwitch ? clientCode : undefined;

        // RequireAuth already hydrates the requested client on its first pass,
        // so re-hydrating here would be a second full user + metadata fetch.
        // Only sign in when the active context doesn't already match.
        const alreadyOnClient =
          !!effectiveClientCode &&
          context.code?.toUpperCase() === effectiveClientCode;

        if (!alreadyOnClient) {
          await signInWithClient(
            signInUser,
            user.email ?? '',
            effectiveClientCode
          );
        }

        const stateObj = location.state as { from?: string };
        const redirectTo =
          stateObj?.from && stateObj.from !== '/login' ? stateObj.from : '/';

        // The /client/:code path is the source of truth for the active client,
        // so the destination's ?client= must match it — overwriting any stale
        // value left by the previously viewed client (the param persists across
        // navigation). Otherwise the destination's URL->context sync would read
        // the stale code and switch the session back to the old client.
        const params = new URLSearchParams(location.search);
        if (effectiveClientCode) {
          params.set('client', effectiveClientCode);
        } else {
          params.delete('client');
        }
        const search = params.toString();

        navigate(`${redirectTo}${search ? `?${search}` : ''}`, {
          replace: true,
        });
      };

      handleSignIn();
    }
  }, [
    user,
    isAuthenticated,
    isUserLoaded,
    canClientSwitch,
    context.code,
    location.state,
    location.search,
    navigate,
    signInUser,
    clientCode,
  ]);

  return (
    <AppShell
      header={{
        height: 64,
      }}
    >
      <AppShell.Header>
        <Flex align="center" h="100%" pl="md">
          <LogoType width={110} withOutline />
        </Flex>
      </AppShell.Header>
      <AppShell.Main>
        <div
          data-testid="login-page"
          style={{
            display: 'grid',
            paddingTop: 90,
            gap: 28,
            gridTemplateColumns: '1fr',
            gridTemplateRows: 'auto',
            justifyItems: 'center',
          }}
        >
          {!error && !isDevelopment && <span>Loading ...</span>}
          {error && <div>{error}</div>}
        </div>
      </AppShell.Main>
    </AppShell>
  );
};

export default ClientSelector;
